import React, { useCallback, useEffect, useState } from "react";
import {
  Alert, Autocomplete, Box, Chip, FormControl, Grid, InputLabel, MenuItem,
  Paper, Select, Stack, Tab, Tabs, TextField, Typography,
} from "@mui/material";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import PersonAddAlt1RoundedIcon from "@mui/icons-material/PersonAddAlt1Rounded";
import { LoadingButton } from "@mui/lab";
import { MuiTelInput } from "mui-tel-input";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import CountrySelector from "../../widgets/CountrySelector";
import StateSelector from "../../widgets/StateSelector";
import CitySelector from "../../widgets/CitySelector";
import { toastError } from "../../utils/util";
import { API_ENDPOINT, ONSITE_MODULE_ITEMS } from "../../utils/constants";
import { getOnsiteHeader, isOnsiteActivated } from "./utils";
import { printRow } from "./ImtexBadgeDimension";

export const SPOT_REG_CATEGORIES = [
  "EXHIBITOR", "DELEGATION", "VIP", "EC", "SERVICE", "MEDIA", "STUDENT", "VISITOR",
];

const EMPTY_REGISTRATION = {
  title: "", firstName: "", lastName: "", designation: "", mobileNo: "",
  email: "", companyName: "", address: "", country: null, state: null,
  city: null, pinZip: "", website: "", telephoneNo: "",
};

const CATEGORY_LABELS = {
  EXHIBITOR: "Exhibitor",
  DELEGATION: "Delegation",
  VIP: "Special Invitee",
  EC: "EC",
  SERVICE: "Service",
  MEDIA: "Media",
  STUDENT: "Student",
  VISITOR: "Visitor",
};

const EXTERNAL_REGISTRATIONS = {
  DELEGATION: { linkKey: "tdLink", label: "Trade delegates registration" },
  STUDENT: { linkKey: "studentRegLink", label: "Student registration" },
  VISITOR: { linkKey: "visitorLink", label: "Visitor registration" },
};

const DESIGNATIONS = [
  "Chairman", "Managing Director", "CEO", "President", "Country Head",
  "Executive Director", "Vice President", "Sr. Vice President", "CTO", "COO",
  "CFO", "General Manager", "Sr. Director", "Director", "Deputy Director",
  "Manager", "Production Head", "Technician", "Engineer", "Machine Operator",
  "Production/ Plant/ Technical Head", "Trainees", "Consultant", "Executive",
  "Partner", "Advisor", "Programmer", "Contractor",
];

const SpotRegistration = () => {
  const navigate = useNavigate();
  const [registration, setRegistration] = useState(EMPTY_REGISTRATION);
  const [selectedTab, setSelectedTab] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [registrationLinks, setRegistrationLinks] = useState(null);
  const [linksLoading, setLinksLoading] = useState(true);
  const [linksError, setLinksError] = useState(false);
  const selectedCategory = SPOT_REG_CATEGORIES[selectedTab];

  useEffect(() => {
    if (!isOnsiteActivated(ONSITE_MODULE_ITEMS.ONSITE_REGISTRATION)) {
      navigate(-1);
      return undefined;
    }

    const controller = new AbortController();
    const fetchRegistrationLinks = async () => {
      try {
        const { data } = await axios.post(
          `${API_ENDPOINT}/onsite/get-reg-links`,
          {},
          {
            ...getOnsiteHeader(ONSITE_MODULE_ITEMS.ONSITE_REGISTRATION),
            signal: controller.signal,
          }
        );
        setRegistrationLinks(data);
        setLinksError(false);
      } catch (error) {
        if (error.code !== "ERR_CANCELED") setLinksError(true);
      } finally {
        if (!controller.signal.aborted) setLinksLoading(false);
      }
    };

    fetchRegistrationLinks();
    return () => controller.abort();
  }, [navigate]);

  const updateRegistration = useCallback((field, value) => {
    setRegistration((current) => ({ ...current, [field]: value }));
  }, []);

  const redirectToRegistration = (category) => {
    const externalRegistration = EXTERNAL_REGISTRATIONS[category];
    if (linksLoading) {
      toast.info("Registration links are still loading. Please try again.");
      return;
    }
    if (linksError) {
      toast.error("Registration links are unavailable. Please refresh and try again.");
      return;
    }

    const link = registrationLinks?.[externalRegistration.linkKey];
    if (!link) {
      toast.error(`${externalRegistration.label} link is not configured.`);
      return;
    }
    window.open(link, "_blank", "noopener,noreferrer");
  };

  const handleTabChange = (event, newValue) => {
    const nextCategory = SPOT_REG_CATEGORIES[newValue];
    if (EXTERNAL_REGISTRATIONS[nextCategory]) {
      redirectToRegistration(nextCategory);
      return;
    }
    setSelectedTab(newValue);
  };

  const handleCountryChange = useCallback((country) => {
    setRegistration((current) => ({ ...current, country, state: null, city: null }));
  }, []);

  const handleStateChange = useCallback((state) => {
    setRegistration((current) => ({ ...current, state, city: null }));
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API_ENDPOINT}/onsite/spot-register?type=${selectedCategory}`,
        registration,
        getOnsiteHeader(ONSITE_MODULE_ITEMS.ONSITE_REGISTRATION)
      );
      toast.success(data.message);
      setTimeout(() => {
        printRow(data.user);
        setRegistration(EMPTY_REGISTRATION);
      }, 500);
    } catch (error) {
      toastError(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1120, mx: "auto", pb: 4 }}>
      <Paper variant="outlined" sx={{ mb: 3, overflow: "hidden", borderRadius: 3 }}>
        <Box sx={{ p: { xs: 2, sm: 3 }, pb: { xs: 1.5, sm: 2 } }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="h5" fontWeight={700}>Onsite registration</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Select a badge category to register an attendee or continue to an online registration form.
              </Typography>
            </Box>
            <Chip
              icon={<PersonAddAlt1RoundedIcon />}
              label="All form fields are required"
              color="primary"
              variant="outlined"
              size="small"
            />
          </Stack>
        </Box>

        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          aria-label="Registration categories"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            px: { xs: 1, sm: 2 }, borderTop: 1, borderColor: "divider", bgcolor: "grey.50",
            "& .MuiTab-root": { minHeight: 58, textTransform: "none" },
          }}
        >
          {SPOT_REG_CATEGORIES.map((category) => {
            const isExternal = Boolean(EXTERNAL_REGISTRATIONS[category]);
            return (
              <Tab
                key={category}
                aria-label={CATEGORY_LABELS[category]}
                label={
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <span>{CATEGORY_LABELS[category]}</span>
                    {isExternal && <OpenInNewRoundedIcon sx={{ fontSize: 16 }} />}
                  </Stack>
                }
              />
            );
          })}
        </Tabs>
      </Paper>

      {linksError && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
          Online registration links could not be loaded. Onsite registration is still available;
          refresh the page before using an external registration tab.
        </Alert>
      )}

      <Paper
        component="form"
        onSubmit={handleSubmit}
        variant="outlined"
        sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}
      >
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" fontWeight={700}>
            {CATEGORY_LABELS[selectedCategory]} details
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Enter the attendee information below. A badge will be ready to print after successful registration.
          </Typography>
        </Box>

        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={2}>
            <FormControl required size="small" fullWidth>
              <InputLabel>Title</InputLabel>
              <Select
                required
                value={registration.title}
                label="Title"
                onChange={(event) => updateRegistration("title", event.target.value)}
              >
                {["Mr.", "Mrs.", "Ms.", "Miss.", "Dr.", "Prof."].map((item) => (
                  <MenuItem key={item} value={item}>{item}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={5}>
            <TextField
              size="small" required label="First Name" value={registration.firstName}
              onChange={(event) => updateRegistration("firstName", event.target.value)} fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={5}>
            <TextField
              size="small" required label="Last Name" value={registration.lastName}
              onChange={(event) => updateRegistration("lastName", event.target.value)} fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={DESIGNATIONS}
              value={registration.designation}
              onChange={(event, newValue) => updateRegistration("designation", newValue)}
              renderInput={(params) => <TextField {...params} label="Designation" required size="small" />}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              size="small" required label="Company Name" value={registration.companyName}
              onChange={(event) => updateRegistration("companyName", event.target.value)} fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              size="small" required label="Address" value={registration.address}
              onChange={(event) => updateRegistration("address", event.target.value)}
              fullWidth multiline minRows={2}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <CountrySelector
              required selectedCountryCode="" selectedCountry={registration.country}
              onChange={handleCountryChange}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <StateSelector
              required countryCode={registration.country?.code}
              selectedState={registration.state} onChange={handleStateChange}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <CitySelector
              required selectedCity={registration.city}
              onChange={(city) => updateRegistration("city", city)}
              countryCode={registration.country?.code || null}
              stateCode={registration.state?.code || null}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              size="small" required label="PIN / ZIP" value={registration.pinZip}
              onChange={(event) => updateRegistration("pinZip", event.target.value)} fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              size="small" required label="Website" type="url" placeholder="https://example.com"
              value={registration.website}
              onChange={(event) => updateRegistration("website", event.target.value)} fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              size="small" required label="Email" type="email" value={registration.email}
              onChange={(event) => updateRegistration("email", event.target.value)} fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <MuiTelInput
              required fullWidth size="small" label="Mobile No" defaultCountry="IN" forceCallingCode
              value={registration.mobileNo}
              onChange={(newValue) => updateRegistration("mobileNo", newValue)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              size="small" required label="Telephone No" type="tel" value={registration.telephoneNo}
              onChange={(event) => updateRegistration("telephoneNo", event.target.value)} fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <Stack
              direction={{ xs: "column", sm: "row" }} spacing={1.5}
              alignItems={{ xs: "stretch", sm: "center" }} justifyContent="space-between" sx={{ mt: 1 }}
            >
              <Typography variant="caption" color="text.secondary">
                Please review the details before creating the badge.
              </Typography>
              <LoadingButton
                type="submit" variant="contained" size="large" loading={submitting}
                startIcon={<PersonAddAlt1RoundedIcon />}
                sx={{ minWidth: { sm: 220 }, borderRadius: 2 }}
              >
                Register &amp; print badge
              </LoadingButton>
            </Stack>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default SpotRegistration;
